import React from 'react';
import NodeDashboardPage from '../NodeDashboard/NodeDashboardPage';
import T from '../../localization/LocalizationKeys';
import Icons from '../Common/Icons';
import NodePage from '../NodePage/NodePage';
import ViewChooser from '../Asset/AssetView/ViewChooser';
import ChartView from '../Chart/ChartView';
import AlarmListTabs from '../Alarm/AlarmListTabs';
import { SideBarItemType } from '../NavBar/SideBarItems';
import AdminAssetList from '../Admin/Assets/AdminAssetsList';
import AdminViewAssetPage, { AssetName } from '../Admin/Assets/AdminViewAsset';
import AdminCreateAsset from '../Admin/Assets/AdminCreateAsset';
import AdminProvidersList from '../Admin/Providers/AdminProvidersList';
import AdminProvider, { ProviderName } from '../Admin/Providers/AdminProvider';
import AdminProvisionTemplatesList from '../Admin/ProvisionTemplates/AdminProvisionTemplatesList';
import AdminProvisionTemplatePage, { TemplateName } from '../Admin/ProvisionTemplates/AdminProvisionTemplatePage';
import Home from '../Home';
import ComponentPlayground from '../Model/extra/ComponentPlayground';
import AlarmDefinitionsList from '../Admin/Alarms/AlarmDefinitionsList';
import AlarmView, { AlarmName } from '../Admin/Alarms/AlarmView';
import DashboardsPage from '../Dashboard/DashboardsPage';
import DashboardEditorPage, { DashboardName } from '../Dashboard/DashboardEditorPage';
import AdminUserPageList from '../Admin/Organizations/Users/AdminUserPageList';
import AdminUserPage, { UserName } from 'components/Admin/Organizations/Users/AdminUserPage';
import AdminTreesPage from '../Admin/Tree/AdminTreesPage';
import AdminEditTreePage, { TreeName } from '../Admin/Tree/AdminEditTreePage';
import AdminModelPageList from '../Admin/Model/dtdl/AdminModelPageList';
import AdminModelPage, { ModelName } from '../Admin/Model/dtdl/AdminModelPage';
import BindingDescriptionList from '../Admin/Model/dtdl/BindingDescriptionList';
import AdminBindingPage, { BindingName } from '../Admin/Model/bindings/AdminBindingPage';
import AdminProviderClient, { ProviderClientName } from 'components/Admin/Providers/AdminProviderClient';
import MigrateModel from 'components/Admin/Model/migration/MigrateModel';
import AdminOrganizationPage from 'components/Admin/Organizations/AdminOrganizationPage';
import AdminGroupsPage from 'components/Admin/Organizations/Groups/AdminGroupsPage';
import AdminGroupEditPage, { GroupName } from 'components/Admin/Organizations/Groups/AdminGroupEditPage';
import AdminOrgEditPage, { OrgName } from 'components/Admin/Organizations/AdminOrgEditPage';
import AdminThemeEditorPage from 'components/Admin/ThemeEditor/AdminThemeEditorPage';
import OrgToDashboardRelations from 'components/Admin/Organizations/Relations/OrgToDashboard';
import OrgToUserRelations from 'components/Admin/Organizations/Relations/OrgToUser';
import OrgToGroupRelations from 'components/Admin/Organizations/Relations/OrgToGroup';
import OrgToAssetRelations from 'components/Admin/Organizations/Relations/OrgToAsset';
import OrgToModelsRelations from 'components/Admin/Organizations/Relations/OrgToModels';
import OrgToAlarmRelations from 'components/Admin/Organizations/Relations/OrgToAlarms';
import OrgToTreesRelations from 'components/Admin/Organizations/Relations/OrgToTrees';
import OrgToProvidersRelations from 'components/Admin/Organizations/Relations/OrgToProviders';
import OrgToProviderClientsRelations from 'components/Admin/Organizations/Relations/OrgToProviderClients';
import OrgToNotificationsRelations from 'components/Admin/Organizations/Relations/OrgToNotifications';
import HomeDashboardEditor from 'components/Dashboard/HomeDashboardEditor';
import { privileges } from 'utils/types';
// import ModelComponenTestPage from 'components/Admin/TestPage/ModelComponentTestPage';
// import EditTreeTestPage from 'components/Admin/TestPage/EditTreeTestPage';
import ComponenTestPage from 'components/Admin/TestPage/ComponentTestPage';
import AdminNotificationsPage from 'components/Admin/Notifications/AdminNotificationsPage';
import AdminNotificationEditPage, { NotificationName } from 'components/Admin/Notifications/AdminNotificationEditPage';

/**
 *  Holds all routes and menus. The menu items will be shown in the same order as they are defined here.
 *
 *  To define a menu item the following properties are required:
 *  - name
 *  - icon
 *  - path At least one item in the path. The first item will be used as the link.
 *  The menu item will also generate a route. For more menu item properties @see MenuItemPropType
 *
 *  To define only a route without a menu item just exclude the name and icon from the item.
 *  If a route should be included the item must have the following properties:
 *  - path
 *  - component
 *  The following properties are optional:
 *  - exact
 *  - protected
 *
 *  Example Menu item and Route:
 *  This will define both a menu item and a route:
 *  {
 *     path: ['/somepath'],
 *     component: MyComponent,
 *     exact: true,
 *     protected: true,
 *     name: T.sidebar.item.thetranslation,
 *     icon: Icons.SomeIcon,
 *   },
 *
 *   Example only route without menu item:
 *   {
 *     path: ['/playground'],
 *     component: ComponentPlayground,
 *     exact: true
 *   }
 *
 *   Example only menu item without a route definition
 *    {
 *     type: SideBarItemType.HEADING,
 *     name: T.sidebar.item.admin,
 *     icon: Icons.Tools,
 *     isEnabled: (userData) => userData?.isAdmin
 *   },
 */
const AppRoutesAndMenu = [
  {
    path: ['/personal'],
    component: NodeDashboardPage,
    exact: true,
    protected: true,
    name: T.sidebar.item.home,
    icon: Icons.Home,
    visibleWhenLoggedOut: true
  },
  {
    path: ['/view'],
    component: () => <NodePage><ViewChooser /></NodePage>,
    exact: false,
    protected: true,
    name: T.sidebar.item.assets,
    icon: Icons.Devices
  },
  {
    path: ['/charts'],
    component: () => <NodePage><ChartView /></NodePage>,
    protected: true,
    name: T.sidebar.item.charts,
    icon: Icons.ChartLine
  },
  {
    path: [{
      path: '/alarms',
      name: T.sidebar.item.alarms
    }],
    component: () => <NodePage><AlarmListTabs /></NodePage>,
    protected: true,
    name: T.sidebar.item.alarms,
    icon: Icons.Notification
  },
  {
    type: SideBarItemType.LINK,
    name: T.sidebar.item.analytics,
    href: 'https://insights.timeseries.azure.com/',
    target: '_blank',
    element: 'a',
    icon: Icons.DataVis,
    isEnabled: (userData) => {
      return userData?.useTimeSeriesExplorer;
    }
  },
  {
    type: SideBarItemType.HEADING,
    name: T.sidebar.item.admin,
    icon: Icons.Tools,
    isEnabled: (userData) => userData?.hasPrivilege([
      privileges.Any
    ])
  },
  {
    path: [{
      path: '/admin/assets',
      name: T.common.assets
    }],
    component: AdminAssetList,
    exact: true,
    protected: true,
    name: T.common.assets,
    icon: Icons.Asset,
    isEnabled: (userData) => userData?.hasPrivilege([
      privileges.AssetManagement
    ])
  },
  {
    path: [{
      path: '/admin/assets/new-asset',
      name: T.asset.createText
    }],
    component: AdminCreateAsset,
    exact: true,
    protected: true
  },
  {
    path: [{
      path: '/admin/assets/new-asset-wizard',
      name: T.asset.createText
    }],
    component: AdminCreateAsset,
    exact: true,
    protected: true
  },
  {
    path: [{
      path: '/admin/assets/:assetId',
      name: ({ params }) => <AssetName assetId={params.assetId} />
    }],
    component: AdminViewAssetPage,
    exact: true,
    protected: true
  },
  {
    path: ['/'],
    component: Home,
    exact: true
  },
  {
    path: ['/playground'],
    component: ComponentPlayground,
    exact: true
  },
  {
    path: [{
      path: '/admin/alarms',
      name: T.common.alarms
    }],
    component: AlarmDefinitionsList,
    exact: true,
    protected: true,
    name: T.common.alarms,
    icon: Icons.Notification,
    isEnabled: (userData) => userData?.hasPrivilege([
      privileges.AlarmManagement
    ])
  },
  {
    path: [{
      path: '/admin/alarms/new-def',
      // TODO: Translate
      name: 'New alarm'
    }, {
      path: '/admin/alarms/:alarmId',
      name: ({ params }) => <AlarmName alarmId={params.alarmId} />
    }],
    component: AlarmView,
    exact: true,
    protected: true
  },
  {
    path: [{
      path: '/admin/dashboards',
      name: T.sidebar.item.dashboards
    }],
    component: DashboardsPage,
    exact: true,
    protected: true,
    name: T.sidebar.item.dashboards,
    icon: Icons.Dashboard,
    isEnabled: (userData) => userData?.hasPrivilege([
      privileges.DashboardManagement
    ])
  },
  {
    path: [{
      path: '/admin/dashboards/home',
      name: 'Home Dashboards'
    }],
    component: HomeDashboardEditor,
    exact: true,
    protected: true
  },
  {
    path: [{
      path: '/admin/dashboards/new-dashboard',
      // TODO: Translate
      name: 'New Dashboard'
    },
    {
      path: '/admin/dashboards/:dashboardId',
      name: ({ params }) => <DashboardName dashboardId={params.dashboardId} />
    }],
    component: DashboardEditorPage,
    exact: true,
    protected: true
  },
  {
    path: [{
      path: '/admin/users',
      name: T.sidebar.item.users
    }],
    component: AdminUserPageList,
    exact: true,
    protected: true,
    name: T.sidebar.item.users,
    icon: Icons.User,
    isEnabled: (userData) => userData?.hasPrivilege([
      privileges.UserManagement
    ])
  },
  {
    path: [{
      path: '/admin/users/new-user',
      // TODO: Translate
      name: 'New User'
    },
    {
      path: '/admin/users/:userId',
      name: ({ params }) => <UserName userId={params.userId} />
    }],
    component: AdminUserPage,
    exact: true,
    protected: true
  },
  {
    name: T.sidebar.item.groups,
    path: [{ path: '/admin/groups', name: T.sidebar.item.groups }],
    icon: Icons.Group,
    component: AdminGroupsPage,
    exact: true,
    protected: true,
    isEnabled: userData => userData?.hasPrivilege([
      privileges.UserManagement
    ])
  },
  {
    path: [{
      path: '/admin/groups/new-group',
      name: 'New Group'
    }, {
      path: '/admin/groups/:groupId',
      name: ({ params }) => <GroupName groupId={params.groupId} />
    }],
    component: AdminGroupEditPage,
    exact: true,
    protected: true
  },
  {
    name: T.sidebar.item.notifications,
    path: [{ path: '/admin/notifications', name: T.sidebar.item.notifications }],
    icon: Icons.Messages,
    component: AdminNotificationsPage,
    exact: true,
    protected: true,
    isEnabled: userData => userData?.hasPrivilege([
      privileges.NotificationManagement
    ])
  },
  {
    path: [{
      path: '/admin/notifications/new-notification',
      name: 'New Notification'
    }, {
      path: '/admin/notifications/:notificationId',
      name: ({ params }) => <NotificationName notificationId={params.notificationId} />
    }],
    component: AdminNotificationEditPage,
    exact: true,
    protected: true
  },
  {
    name: T.sidebar.item.organizations,
    path: [{ path: '/admin/organizations', name: T.sidebar.item.organizations }],
    component: AdminOrganizationPage,
    icon: Icons.Enterprise,
    exact: true,
    protected: true,
    isEnabled: userData => userData?.hasPrivilege([
      privileges.OrganizationManagement
    ])
  },
  {
    path: [
      {
        path: '/admin/organizations/new',
        name: 'New Organization'
      },
      {
        path: '/admin/organizations/:orgId',
        name: ({ params }) => <OrgName orgId={params.orgId} />
      }
    ],
    component: AdminOrgEditPage,
    exact: true,
    protected: true
  },
  {
    path: [
      {
        path: '/admin/organizations/:orgId/dashboards',
        name: T.permissions.relations.dashboard
      }
    ],
    component: OrgToDashboardRelations,
    exact: true,
    protected: true
  },
  {
    path: [
      {
        path: '/admin/organizations/:orgId/assets',
        name: T.permissions.relations.asset
      }
    ],
    component: OrgToAssetRelations,
    exact: true,
    protected: true
  },
  {
    path: [
      {
        path: '/admin/organizations/:orgId/alarms',
        name: T.permissions.relations.alarm
      }
    ],
    component: OrgToAlarmRelations,
    exact: true,
    protected: true
  },
  {
    path: [
      {
        path: '/admin/organizations/:orgId/trees',
        name: T.permissions.relations.tree
      }
    ],
    component: OrgToTreesRelations,
    exact: true,
    protected: true
  },
  {
    path: [
      {
        path: '/admin/organizations/:orgId/users',
        name: T.permissions.relations.user
      }
    ],
    component: OrgToUserRelations,
    exact: true,
    protected: true
  },
  {
    path: [
      {
        path: '/admin/organizations/:orgId/groups',
        name: T.permissions.relations.group
      }
    ],
    component: OrgToGroupRelations,
    exact: true,
    protected: true
  },
  {
    path: [
      {
        path: '/admin/organizations/:orgId/models',
        name: T.permissions.relations.model
      }
    ],
    component: OrgToModelsRelations,
    exact: true,
    protected: true
  },
  {
    path: [
      {
        path: '/admin/organizations/:orgId/providers',
        name: T.permissions.relations.provider
      }
    ],
    component: OrgToProvidersRelations,
    exact: true,
    protected: true
  },
  {
    path: [
      {
        path: '/admin/organizations/:orgId/providers/:providerId/provider_clients',
        name: T.permissions.relations.providerClient
      }
    ],
    component: OrgToProviderClientsRelations,
    exact: true,
    protected: true
  },
  {
    path: [
      {
        path: '/admin/organizations/:orgId/notifications',
        name: T.permissions.relations.notification
      }
    ],
    component: OrgToNotificationsRelations,
    exact: true,
    protected: true
  },
  {
    path: [{
      path: '/admin/trees',
      name: T.sidebar.item.trees
    }],
    component: AdminTreesPage,
    exact: true,
    protected: true,
    name: T.sidebar.item.trees,
    icon: Icons.TreeViewAlt,
    isEnabled: (userData) => userData?.hasPrivilege([
      privileges.TreeManagement
    ])
  },
  {
    path: [{
      path: '/admin/trees/new-tree',
      // TODO: Translate
      name: 'New Tree'
    },
    {
      path: '/admin/trees/:treeId',
      name: ({ params }) => <TreeName treeId={params.treeId} />
    }],
    component: AdminEditTreePage,
    exact: true,
    protected: true
  },
  {
    path: [{
      path: '/admin/models',
      name: T.sidebar.item.models
    }],
    component: AdminModelPageList,
    exact: true,
    protected: true,
    name: T.sidebar.item.models,
    icon: Icons.DtdlModel,
    isEnabled: userData => userData?.hasPrivilege([
      privileges.ModelManagement
    ])
  },
  {
    path: [
      {
        path: '/admin/models/new-model',
        name: 'New Model'
      },
      {
        path: '/admin/models/:modelId',
        name: ({ params, search }) => <ModelName modelId={params.modelId} search={search}/>
      }
    ],
    component: AdminModelPage,
    exact: true,
    protected: true
  },
  {
    path: [{
      path: '/admin/models/:modelId/bindings',
      name: T.sidebar.item.bindings
    }],
    component: BindingDescriptionList,
    exact: true,
    protected: true
  },
  {
    path: [{
      path: '/admin/models/:modelId/bindings/new-binding',
      // TODO: Translate
      name: 'New Binding'
    }, {
      path: '/admin/models/:modelId/bindings/:bindingId',
      name: ({ params }) => <BindingName modelId={params.modelId} bindingId={params.bindingId} />
    }],
    component: AdminBindingPage,
    exact: true,
    protected: true
  },
  {
    path: [{
      path: '/admin/providers',
      name: T.common.providers
    }],
    component: AdminProvidersList,
    exact: true,
    protected: true,
    name: T.sidebar.item.providers,
    icon: Icons.Provider,
    isEnabled: (userData) => userData?.hasPrivilege([
      privileges.ProviderManagement
    ])
  },
  {
    path: [{
      path: '/admin/providers/new-provider',
      // TODO: Translation
      name: 'New provider'
    }, {
      path: '/admin/providers/:providerId',
      name: ({ params }) => <ProviderName providerId={params.providerId} />
    }],
    component: AdminProvider,
    exact: true,
    protected: true
  },
  {
    path: [{
      path: '/admin/providers/:providerId/clients/new-provider-client',
      // TODO: Translation
      name: 'New provider client'
    }, {
      path: '/admin/providers/:providerId/clients/:providerClientId',
      name: ({ params }) => <ProviderClientName providerId={params.providerId} providerClientId={params.providerClientId} />
    }],
    component: AdminProviderClient,
    exact: true,
    protected: true
  },
  {
    path: [{
      path: '/admin/providers/:providerId/templates',
      name: T.common.templates
    }],
    component: AdminProvisionTemplatesList,
    exact: true,
    protected: true
  },
  {
    path: [{
      path: '/admin/providers/:providerId/templates/new-template',
      // TODO Translation
      name: 'New Template'
    }, {
      path: '/admin/providers/:providerId/templates/:templateId',
      name: ({ params }) => <TemplateName templateId={params.templateId} />
    }],
    component: AdminProvisionTemplatePage,
    exact: true,
    protected: true
  },
  {
    path: [{
      path: '/admin/models/:modelId/templates',
      name: T.common.templates
    }],
    component: AdminProvisionTemplatesList,
    exact: true,
    protected: true
  },
  {
    path: [{
      path: '/admin/models/:modelId/templates/new-template',
      // TODO Translation
      name: 'New Template'
    }, {
      path: '/admin/models/:modelId/templates/:templateId',
      name: ({ params }) => <TemplateName templateId={params.templateId} />
    }],
    component: AdminProvisionTemplatePage,
    exact: true,
    protected: true
  },
  {
    path: ['/admin/templates/new-template', '/admin/templates/:templateId', '/admin/assets/:assetId/templates/:templateId'],
    component: AdminProvisionTemplatePage,
    exact: true,
    protected: true
  },
  {
    path: [{
      path: '/admin/migrate-assets/newModel',
      // TODO: Translate
      name: 'New Model'
    },
    {
      path: '/admin/models/:modelId/migrate',
      // TODO: Translate
      name: 'Migrate Model'
    },
    {
      path: '/admin/assets/:assetId/migrate',
      name: T.migrate.asset
    }],
    component: MigrateModel,
    exact: true,
    protected: true
  },
  {
    path: [{
      path: '/admin/theme'
    }],
    component: AdminThemeEditorPage,
    exact: true,
    protected: true
  },
  // {
  //   path: [{
  //     path: '/testmodelcomponents'
  //   }],
  //   component: ModelComponenTestPage,
  //   exact: true,
  //   protected: true
  // },
  {
    path: [{
      path: '/testcomponent'
    }],
    component: ComponenTestPage,
    exact: true,
    protected: true
  }
  // {
  //   path: [{
  //     path: '/testedittree'
  //   }],
  //   component: EditTreeTestPage,
  //   exact: true,
  //   protected: true
  // }
];

export default AppRoutesAndMenu;
